// Decoders of the sim's read-only views (crates/sim/src/view.rs; docs/platform/ClientShell.md
// "Sim views"). Plain TypeScript with erasable syntax only, so node can load it as well
// (`node --experimental-strip-types`, client/tests).

export type Anim = { clip: string; time: number; weight: number };

export type Poser = {
  facing: number; action: number;
  actionAnim: Anim; gadgetAnim: Anim; gadgetLR: Anim; gadgetState: number; gadgetAim: number;
  visible: boolean; meshPos: number[]; orientation: number[]; scale: number[];
  walkWeight: number; rotation: number; colourAnim: Anim; mesh: string;
};

export type WormView = {
  id: number; index: number; team: number; name: string;
  x: number; y: number; rotation: number; health: number; state: number; flags: number;
  equip: number; aimAngle: number; aimPower: number; crosshairAngle: number; vehicle: number;
  /** `Worm::GetGraphicalPosition` (world units, y up, unlike x, y): in a vehicle its mesh's
   * graphical position plus the worm offset, else the physics position; the HUD's label anchor. */
  graphical: number[];
  poser: Poser | null;
};

/** A BaseMesh of the frame view ("WMV7", crates/sim/src/view.rs `meshes`). `instance`: it has a
 * mesh instance (`m_pMeshInst`; the original draws nothing without one). `layer`: the render
 * layer it was created on (`m_Ld.m_Layer` as its `g_pLayerNames` index, `SceneIdEnum`: 2
 * GameMID_Behind_GUI, 3 GameMID, 4 GameFG, ...; 255 none). `anims`: the instance's
 * `XAnimInstance` states `Evaluate` blends, in its order; `claimed`: clips with a weight that hold
 * no active slot (their attributes are written, not blended). */
export type MeshView = {
  id: number; cls: string; resource: string; mesh: string; source: string;
  pos: number[]; orientation: number[]; scale: number[]; colour: number[];
  visible: boolean; instance: boolean; layer: number; animAngle: number; anims: Anim[]; claimed: string[];
};

/** One particle effect of a step's particle view (`BaseParticleEffect` with effect data). */
export type EffectView = {
  id: number; resource: string; layer: number; pos: number[]; euler: number[]; alpha: number;
  /** `m_Ld.m_Flags` (bit 2 started, bit 5 attached with the locator's transform, bit 9 its
   * LogicUpdate ran, bit 10 fixed rotation). */
  flags: number;
  /** The dirty requests its `GraphicUpdate` served in the step (`enumBaseParticleFlagLogicalToGraphical`). */
  served: number;
  /** `m_ParentInfo` (`AttachToThisLocator`): the parent `BaseMesh` object (0xffffffff: none),
   * its locator, the additional rotation (radians) and the reverse-rotation flag. */
  parent: number; locator: string; parentRotation: number; parentReverse: boolean;
};

/** A `TransientParticleEffect::GraphicUpdate` that found requests (`m_DirtyFlags`: 1 initialise,
 * 2 start, 4 set position, 8 stop). */
export type TransientView = { id: number; dirty: number; effect: number; layer: number; pos: number[] };

/** A step's particle view ("WMF2", crates/sim/src/view.rs `particles`). */
export type ParticleStep = {
  tick: number; waterLevel: number;
  /** `WS_RunTimeSceneGraph::m_WindDirection.x`. */
  wind: number;
  /** The theme's wind effect (`BackgroundMan::m_WindParticlehandle`), 0xffffffff without one. */
  windEffect: number;
  effects: EffectView[];
  transients: TransientView[];
};

export type EntityView = {
  id: number; cls: string; x: number; y: number; rotation: number; radius: number;
  payload: number; crateContent: number; crateState: number;
};

/** A presentation event of a step (crates/sim/src/view.rs "events"). Sound events (kinds 0..8):
 * `pos` (kind SOUND_POS: the instance's 3-D position in the camera's frame), `team` (kind
 * SOUND_TEAM: the requester of a press refusal) and `param` (kind SOUND_PARAM: an event
 * parameter's value; `byName`: FMOD's case-insensitive name lookup of `AutoPlaySound`, else
 * `XSoundInstance::GetParameterIndex`'s exact name or the primary parameter); SOUND_MUTE
 * (`StopAutoPlaySound`) names the event only. LINE (`BulletTracers::DrawTracerLine`, the render
 * manager's line of step `tick`) carries `line`; EFFECT_MESH (an `AngledGadget::GraphicUpdate`
 * operation on a muzzle or cartridge mesh, gunfx.ts) carries `fx`; both leave the sound fields
 * empty. */
export type SoundEvent = {
  kind: number; path: string; event: number; instance: number; serial: number; pos?: [number, number, number]; team?: number;
  param?: { name: string; value: number; byName: boolean };
  /** SOUND_VOLUME: the instance's event volume. */
  volume?: number;
  /** SOUND_AUDIO: an audio-manager or `SoundResourceMan` call (crates/sim/src/view/events.rs
   * `AudioCall`, AUDIO_* ops): `a`, `b` its small arguments, `event`/`path` a track's event,
   * `value` two f32, `raw0` the first one's bits. */
  audio?: { op: number; a: number; b: number; value: [number, number]; raw0: number };
  line?: LineEvent; fx?: EffectMeshEvent;
};

/** A tracer line: world end points (z 20), width in pixels, RGBA, `g_pLayerNames` index. */
export type LineEvent = { tick: number; start: number[]; end: number[]; width: number; colour: number[]; layer: number };

/** Operation `op` (FX_*) of step `tick` on effect mesh `slot` (0 the muzzle fire, 1 + c cartridge
 * c) of gadget `gadget`. FX_INIT: `name` is the mesh; FX_PLAY: `name` is the locator of the
 * target (FX_WORM: the held gadget mesh of the worm `object`; FX_VEHICLE: the BaseMesh `object`);
 * FX_HIDE with FX_WORM: the muzzle also leaves that worm's gadget mesh. */
export type EffectMeshEvent = { tick: number; gadget: number; slot: number; op: number; target: number; object: number; name: string };

/** A rope spec `NinjaRopeGraphicsMan::GraphicUpdate` drew (the frame view's rope tail): its index
 * into `m_RopeSpecs` and its points, legacy x, y pairs (render/ropes.ts). */
export type RopeView = { spec: number; points: Float32Array };

/** A mesh instance of a worm poser (the frame view's poses tail, crates/sim/src/view/poses.rs):
 * its `m_bVisible`, the bundle mesh and the node of the worm mesh it hangs from (attachments
 * only), and its `XAnimInstance` states (`anims` blended in slot order, `claimed` as a
 * MeshView's). The worm mesh also carries the position, orientation (Euler radians) and scale
 * `WormPoser::UpdatePoserGraphical` gave it in the step's graphic walk (the logical placement of
 * the step before: the original draws a frame with what its graphic pass set before the frame's
 * logic tick). */
export type PoseInst = {
  visible: boolean; mesh: string; source: string; node: string; anims: Anim[]; claimed: string[];
  transform: { pos: V3; orientation: V3; scale: V3 } | null;
};
/** The instances `WormPoser::UpdatePoserGraphical` drives (crates/sim/src/worm/
 * worm_poser_graphical.rs): the worm mesh, the held gadget, the gadget hat, the death plunger;
 * null where there is no instance. */
export type WormPoses = { worm: PoseInst | null; gadget: PoseInst | null; hat: PoseInst | null; detonator: PoseInst | null };

/** One mesh instance's clip states after a step (the anim view, "WMA1"): the presentation step
 * that created its `XAnimInstance` (a new value is a new instance) and its states. */
export type AnimStep = { created: number; anims: Anim[]; claimed: string[] };
/** The anim view of one step (crates/sim/src/view/poses.rs `anims_cached`): worm object -> its
 * worm mesh, gadget, gadget hat and death plunger (null: none), BaseMesh object -> its instance. */
export type AnimView = { tick: number; worms: Map<number, (AnimStep | null)[]>; meshes: Map<number, AnimStep> };

type V3 = [number, number, number];
/** The frame view's widget tail (crates/sim/src/view/widgets.rs): the presentation members the
 * setters of the HUD aim widgets wrote (`present::hud::hud_widgets`). `dirty`/`once` are the
 * presented frame of the last clip request (0: none). */
export type Widgets = {
  /** `WormMan::IsWormHiddingFromLocalPlayer(-1)`'s inputs: the match can hide (challenge,
   * campaign, bonus or a network game), the current worm's team (-1: none), its label visible,
   * its team AI. */
  hide: { capable: boolean; team: number; labelVisible: boolean; ai: boolean };
  reticle: { active: boolean; pos: V3; offset: V3; rotation: V3; scale: V3; dirty: number } | null;
  powerBar: { active: boolean; pos: V3; rotation: V3; power: number } | null;
  cursor: { valid: boolean; active: boolean; hidden: boolean; pos: V3; offset: V3; type: number; direction: number; phase: number; dirty: number } | null;
  fuseTimer: { active: boolean; pos: V3; time: number } | null;
  gadgetIcon: { active: boolean; pos: V3; equip: number } | null;
  select: { active: boolean; pos: V3; worm: number; once: number } | null;
  /** WormHUDActivity's inputs: `m_bIsActive`, CameraMan::GetCameraBounds (bottom left, top
   * right) and ActivityMan::GetTrackableObjects(kInterest_Offscreen, kInterest_Max) in index
   * order: handle, GetTrackableInterest, Peek(FCC_Pos2) position, WormHUDMan::IsIgnoringHandle. */
  activity: { active: boolean; bl: [number, number]; tr: [number, number]; objects: ActivityObject[] } | null;
};
export type ActivityObject = { index: number; sub: number; interest: number; x: number; y: number; ignored: boolean };

export type FrameView = {
  tick: number; logicFrame: number; logicTime: number; phase: number;
  /** The presented frame of the bake's end (`WS_System::OnGraphicalIdle_BakeFinished`), which
   * starts the theme objects' clips (0 before it). */
  bakeTick: number;
  currentWorm: number; currentTeam: number; turnTimer: number; roundTimer: number;
  wind: number; maxWind: number; waterLevel: number; turnState: number; turnFlags: number;
  teams: { health: number; maxHealth: number }[];
  worms: WormView[]; meshes: MeshView[]; entities: EntityView[];
  events: SoundEvent[]; landId: number; landSum: number;
  /** Worm object id -> its poser's mesh instances (renderer.ts `syncWorm`). */
  poses: Map<number, WormPoses>;
  /** The HUD aim and targeting widgets (crates/sim/src/view/widgets.rs; game/widgets.ts). */
  widgets: Widgets;
  /** The rope specs the last `NinjaRopeGraphicsMan::GraphicUpdate` drew (render/ropes.ts). */
  ropes: RopeView[];
  /** The camera views ("WMC1") of every sim step since the previous posted frame, in order
   * (crates/sim/src/view/camera.rs; the worker collects them, fast-forward included). */
  cams?: Uint8Array[];
  /** The particle views ("WMF1") of every sim step since the previous posted frame, in order. */
  fx?: Uint8Array[];
  /** The anim views ("WMA1") of every sim step since the previous posted frame, in order: the
   * renderer evaluates each step's states (render/meshes.ts `poseSteps`). */
  anims?: Uint8Array[];
};

export const SOUND_START = 0, SOUND_STOP = 1, SOUND_FADE = 2, SOUND_PAUSE = 3, SOUND_RESUME = 4, SOUND_POS = 5, SOUND_TEAM = 6, SOUND_PARAM = 7, SOUND_MUTE = 8,
  LINE = 9, EFFECT_MESH = 10, SOUND_AUDIO = 11, SOUND_VOLUME = 12;
/** SoundEvent.audio ops (crates/sim/src/view/events.rs AUDIO_*). */
export const AUDIO_CATEGORY_VOLUME = 0, AUDIO_CATEGORY_PAUSE = 1, AUDIO_TRACK_LOAD = 2, AUDIO_TRACK_QUEUE = 3, AUDIO_TRACK_PLAY = 4,
  AUDIO_TRACK_FADE_OUT = 5, AUDIO_TRACK_POSITION = 6, AUDIO_ALL_TYPES = 0xff;
/** EffectMeshEvent ops and targets (crates/sim/src/view/events.rs FX_*). */
export const FX_INIT = 0, FX_PLAY = 1, FX_HIDE = 2, FX_RELEASE = 3, FX_KEEP = 0, FX_WORM = 1, FX_VEHICLE = 2;

/** Worm::FlagsEnum bits of WormView.flags. */
export const WORM_CURRENT = 1 << 0, WORM_CROSSHAIR = 1 << 5, WORM_GRAVESTONE = 1 << 6, WORM_SURRENDERED = 1 << 7;

const td = new TextDecoder();

class R {
  dv: DataView; o = 0; u8a: Uint8Array;
  constructor(b: Uint8Array) { this.u8a = b; this.dv = new DataView(b.buffer, b.byteOffset, b.byteLength); }
  u8() { return this.dv.getUint8(this.o++); }
  u16() { const v = this.dv.getUint16(this.o, true); this.o += 2; return v; }
  u32() { const v = this.dv.getUint32(this.o, true); this.o += 4; return v; }
  i32() { const v = this.dv.getInt32(this.o, true); this.o += 4; return v; }
  f32() { const v = this.dv.getFloat32(this.o, true); this.o += 4; return v; }
  fs(n: number) { const a: number[] = []; for (let i = 0; i < n; i++) a.push(this.f32()); return a; }
  /** `u16` length + UTF-8 bytes. Names are short and ASCII: those skip the TextDecoder call. */
  str() {
    const n = this.u16(), a = this.u8a, o = this.o;
    this.o += n;
    if (n > 32) return td.decode(a.subarray(o, o + n));
    let s = '';
    for (let i = o; i < o + n; i++) {
      if (a[i] >= 0x80) return td.decode(a.subarray(o, o + n));
      s += String.fromCharCode(a[i]);
    }
    return s;
  }
  magic(m: string) { const s = td.decode(this.u8a.subarray(this.o, this.o + 4)); this.o += 4; if (s !== m) throw new Error(`view: expected ${m}, got ${s}`); }
}

function anim(r: R): Anim { return { clip: r.str(), time: r.f32(), weight: r.f32() }; }

/** A step's view ("WMT3", crates/sim/src/view.rs `step`): the frame view's turn values the HUD's
 * state machine reads (`HudStep`), its land version and its sound events, for every step the
 * worker observes (the frame view itself goes only with the frame the page shows). */
export type StepView = {
  tick: number; phase: number; currentWorm: number; currentTeam: number; wind: number; turnFlags: number;
  health: number[]; maxHealth: number[]; landId: number; landSum: number; events: SoundEvent[];
};

export function decodeStep(b: Uint8Array): StepView {
  const r = new R(b);
  r.magic('WMT3');
  const s: StepView = {
    tick: r.u32(), phase: r.u32(), currentWorm: r.i32(), currentTeam: r.i32(), wind: r.f32(), turnFlags: r.u8(),
    health: [], maxHealth: [], landId: 0, landSum: 0, events: [],
  };
  for (let n = r.u8(), i = 0; i < n; i++) { s.health.push(r.u32()); s.maxHealth.push(r.u32()); }
  s.landId = r.u32(); s.landSum = r.u32();
  s.events = soundEvents(r);
  return s;
}

/** The presentation events section of the frame and step views. */
function soundEvents(r: R): SoundEvent[] {
  const events: SoundEvent[] = [];
  for (let n = r.u16(), i = 0; i < n; i++) {
    const kind = r.u8();
    if (kind === LINE) {
      const line: LineEvent = { tick: r.u32(), start: r.fs(3), end: r.fs(3), width: r.f32(), colour: r.fs(4), layer: r.u8() };
      events.push({ kind, path: '', event: -1, instance: -1, serial: -1, line });
      continue;
    }
    if (kind === EFFECT_MESH) {
      const fx: EffectMeshEvent = { tick: r.u32(), gadget: r.u32(), slot: r.u8(), op: r.u8(), target: r.u8(), object: r.u32(), name: r.str() };
      events.push({ kind, path: '', event: -1, instance: -1, serial: -1, fx });
      continue;
    }
    if (kind === SOUND_AUDIO) {
      const op = r.u8(), a = r.u8(), b = r.u8(), event = r.u32(), path = r.str();
      const raw0 = r.dv.getUint32(r.o, true);
      const value: [number, number] = [r.f32(), r.f32()];
      events.push({ kind, path, event, instance: -1, serial: -1, audio: { op, a, b, value, raw0 } });
      continue;
    }
    const e: SoundEvent = { kind, path: r.str(), event: r.u32(), instance: r.u32(), serial: r.u32() };
    if (e.kind === SOUND_POS) e.pos = [r.f32(), r.f32(), r.f32()];
    else if (e.kind === SOUND_TEAM) e.team = r.u32();
    else if (e.kind === SOUND_PARAM) e.param = { name: r.str(), value: r.f32(), byName: r.u8() === 1 };
    else if (e.kind === SOUND_VOLUME) e.volume = r.f32();
    events.push(e);
  }
  return events;
}

export function decodeFrame(b: Uint8Array): FrameView {
  const r = new R(b);
  r.magic('WMV7');
  const v: FrameView = {
    tick: r.u32(), logicFrame: r.u32(), logicTime: r.f32(), phase: r.u32(), bakeTick: r.u32(),
    currentWorm: r.i32(), currentTeam: r.i32(), turnTimer: r.f32(), roundTimer: r.f32(),
    wind: r.f32(), maxWind: r.f32(), waterLevel: r.f32(), turnState: r.u32(), turnFlags: r.u8(),
    teams: [], worms: [], meshes: [], entities: [], events: [], landId: 0, landSum: 0, ropes: [], poses: new Map(),
    widgets: { hide: { capable: false, team: -1, labelVisible: true, ai: false }, reticle: null, powerBar: null, cursor: null, fuseTimer: null, gadgetIcon: null, select: null, activity: null },
  };
  for (let n = r.u8(), i = 0; i < n; i++) v.teams.push({ health: r.u32(), maxHealth: r.u32() });
  for (let n = r.u16(), i = 0; i < n; i++) {
    const w: WormView = {
      // Worm::m_TeamIndex counts from 1 (hb: Alpha's worms 1, Bravo's 2); teams[] and phases count from 0.
      id: r.u32(), index: r.u32(), team: r.i32() - 1, name: r.str(), x: r.f32(), y: r.f32(), rotation: r.f32(),
      health: r.i32(), state: r.u32(), flags: r.u32(), equip: r.u32(), aimAngle: r.f32(), aimPower: r.f32(),
      crosshairAngle: r.f32(), vehicle: r.u32(), graphical: r.fs(3), poser: null,
    };
    if (r.u8()) {
      w.poser = {
        facing: r.u32(), action: r.u32(), actionAnim: anim(r), gadgetAnim: anim(r), gadgetLR: anim(r),
        gadgetState: r.u32(), gadgetAim: r.f32(), visible: !!r.u8(), meshPos: r.fs(3), orientation: r.fs(3),
        scale: r.fs(3), walkWeight: r.f32(), rotation: r.f32(), colourAnim: anim(r), mesh: r.str(),
      };
    }
    v.worms.push(w);
  }
  for (let n = r.u16(), i = 0; i < n; i++) {
    const m: MeshView = {
      id: r.u32(), cls: r.str(), resource: r.str(), mesh: r.str(), source: r.str(),
      pos: r.fs(3), orientation: r.fs(3), scale: r.fs(3), colour: r.fs(4), visible: false, instance: false, layer: 255, animAngle: 0, anims: [], claimed: [],
    };
    const flags = r.u8();
    m.visible = (flags & 1) !== 0;
    m.instance = (flags & 2) !== 0;
    m.layer = r.u8();
    m.animAngle = r.f32();
    for (let k = r.u8(), j = 0; j < k; j++) m.anims.push(anim(r));
    for (let k = r.u8(), j = 0; j < k; j++) m.claimed.push(r.str());
    v.meshes.push(m);
  }
  for (let n = r.u16(), i = 0; i < n; i++) {
    v.entities.push({ id: r.u32(), cls: r.str(), x: r.f32(), y: r.f32(), rotation: r.f32(), radius: r.f32(), payload: r.u32(), crateContent: r.i32(), crateState: r.u32() });
  }
  v.events = soundEvents(r);
  v.landId = r.u32(); v.landSum = r.u32();
  // The widget tail (crates/sim/src/view/widgets.rs).
  const v3 = (): V3 => [r.f32(), r.f32(), r.f32()];
  const wd = v.widgets;
  wd.hide = { capable: !!r.u8(), team: r.i32(), labelVisible: !!r.u8(), ai: !!r.u8() };
  for (let n = r.u8(), i = 0; i < n; i++) {
    const tag = r.u8(), len = r.u16(), end = r.o + len;
    if (tag === 1) wd.reticle = { active: !!r.u8(), pos: v3(), offset: v3(), rotation: v3(), scale: v3(), dirty: r.u32() };
    else if (tag === 2) wd.powerBar = { active: !!r.u8(), pos: v3(), rotation: v3(), power: r.f32() };
    else if (tag === 3) wd.cursor = { valid: !!r.u8(), active: !!r.u8(), hidden: !!r.u8(), pos: v3(), offset: v3(), type: r.u32(), direction: r.u32(), phase: r.u32(), dirty: r.u32() };
    else if (tag === 4) wd.fuseTimer = { active: !!r.u8(), pos: v3(), time: r.f32() };
    else if (tag === 5) wd.gadgetIcon = { active: !!r.u8(), pos: v3(), equip: r.u32() };
    else if (tag === 6) wd.select = { active: !!r.u8(), pos: v3(), worm: r.u32(), once: r.u32() };
    else if (tag === 7) {
      const active = !!r.u8(), bl: [number, number] = [r.f32(), r.f32()], tr: [number, number] = [r.f32(), r.f32()];
      const objects: ActivityObject[] = [];
      for (let k = r.u16(); k > 0; k--) objects.push({ index: r.u32(), sub: r.u32(), interest: r.u32(), x: r.f32(), y: r.f32(), ignored: !!r.u8() });
      wd.activity = { active, bl, tr, objects };
    }
    r.o = end;
  }
  // The rope tail (crates/sim/src/view/ropes.rs): u8 n, per spec u8 index, u16 count, f32 x, y.
  for (let n = r.u8(), i = 0; i < n; i++) {
    const spec = r.u8(), count = r.u16();
    v.ropes.push({ spec, points: new Float32Array(r.fs(2 * count)) });
  }
  // The poses tail (crates/sim/src/view/poses.rs): per worm u32 object, then the worm mesh, the
  // gadget, the hat and the plunger: u8 flags (1 exists, 2 visible), the worm mesh's transform,
  // the attachments' mesh, source and node, the states as the meshes'.
  for (let n = r.u16(), i = 0; i < n; i++) {
    const id = r.u32();
    const inst = (attachment: boolean): PoseInst | null => {
      const flags = r.u8();
      if (!(flags & 1)) return null;
      const p: PoseInst = { visible: (flags & 2) !== 0, mesh: '', source: '', node: '', anims: [], claimed: [], transform: null };
      if (attachment) [p.mesh, p.source, p.node] = [r.str(), r.str(), r.str()];
      else p.transform = { pos: v3(), orientation: v3(), scale: v3() };
      for (let k = r.u8(), j = 0; j < k; j++) p.anims.push(anim(r));
      for (let k = r.u8(), j = 0; j < k; j++) p.claimed.push(r.str());
      return p;
    };
    v.poses.set(id, { worm: inst(false), gadget: inst(true), hat: inst(true), detonator: inst(true) });
  }
  return v;
}

/** Decodes a step's anim view ("WMA1", crates/sim/src/view/poses.rs `anims_cached`). */
export function decodeAnims(b: Uint8Array): AnimView {
  const r = new R(b);
  r.magic('WMA1');
  const v: AnimView = { tick: r.u32(), worms: new Map(), meshes: new Map() };
  const step = (): AnimStep => {
    const s: AnimStep = { created: r.u32(), anims: [], claimed: [] };
    for (let k = r.u8(), j = 0; j < k; j++) s.anims.push(anim(r));
    for (let k = r.u8(), j = 0; j < k; j++) s.claimed.push(r.str());
    return s;
  };
  for (let n = r.u16(), i = 0; i < n; i++) {
    const id = r.u32();
    const insts: (AnimStep | null)[] = [];
    for (let k = 0; k < 4; k++) insts.push(r.u8() ? step() : null);
    v.worms.set(id, insts);
  }
  for (let n = r.u16(), i = 0; i < n; i++) v.meshes.set(r.u32(), step());
  return v;
}

/** Decodes a step's particle view ("WMF2"). */
export function decodeParticles(b: Uint8Array): ParticleStep {
  const r = new R(b);
  r.magic('WMF2');
  const s: ParticleStep = { tick: r.u32(), waterLevel: r.f32(), wind: r.f32(), windEffect: r.u32(), effects: [], transients: [] };
  for (let n = r.u16(), i = 0; i < n; i++) {
    s.effects.push({
      id: r.u32(), resource: r.str(), layer: r.u32(), pos: r.fs(3), euler: r.fs(3), alpha: r.f32(), flags: r.u32(), served: r.u32(),
      parent: r.u32(), locator: r.str(), parentRotation: r.f32(), parentReverse: !!r.u8(),
    });
  }
  for (let n = r.u16(), i = 0; i < n; i++) s.transients.push({ id: r.u32(), dirty: r.u32(), effect: r.u32(), layer: r.u32(), pos: r.fs(3) });
  return s;
}

export type ScenePage = { path: string; width: number; height: number };
export type SceneQuad = {
  tier: number; priority: number; z: number; prefabZ: number; order: number; page: number;
  uv: number[]; corners: number[]; flags: number; building: number;
};
export type SceneView = { pages: ScenePage[]; quads: SceneQuad[] };

/** Landscape quad flags. */
export const QUAD_INDESTRUCTIBLE = 1, QUAD_BUILDING = 2, QUAD_BAKEABLE = 4, QUAD_POINT = 8;
/** WS_BAKEDLAYER tiers. */
export const TIER_BG = 1, TIER_MID = 2, TIER_FG = 3;

export function decodeScene(b: Uint8Array): SceneView {
  return sceneOf(new R(b));
}

function sceneOf(r: R): SceneView {
  r.magic('WMS1');
  const pages: ScenePage[] = [];
  for (let n = r.u32(), i = 0; i < n; i++) pages.push({ path: r.str(), width: r.u32(), height: r.u32() });
  const quads: SceneQuad[] = [];
  for (let n = r.u32(), i = 0; i < n; i++) {
    quads.push({ tier: r.u8(), priority: r.i32(), z: r.f32(), prefabZ: r.f32(), order: r.u32(), page: r.u16(), uv: r.fs(4), corners: r.fs(8), flags: r.u32(), building: r.u32() });
  }
  return { pages, quads };
}

/** The lobby's landscape preview ("WMQ1", crates/sim/src/view.rs `preview`): the generator's
 * theme, the theme's `FrontendBackground` (a ui_textures file name, '' when unknown) and the
 * previewer's draws in drawing order (`PPL_RAND_1`..3, each in submission order). */
export type PreviewView = { theme: string; sky: string; scene: SceneView };

export function decodePreview(b: Uint8Array): PreviewView {
  const r = new R(b);
  r.magic('WMQ1');
  const theme = r.str(), sky = r.str();
  return { theme, sky, scene: sceneOf(r) };
}

/** Sim phase (simwasm encoding: kind | detail << 8). */
export type Phase = { kind: 'Boot' | 'Free' | 'Lead' | 'Wait' | 'Over'; team?: number; wait?: 'SaveGame' | 'ExitMatch' | 'Fade' };
export function decodePhase(p: number): Phase {
  const kinds = ['Boot', 'Free', 'Lead', 'Wait', 'Over'] as const;
  const k = kinds[p & 0xff] ?? 'Over';
  if (k === 'Lead') return { kind: k, team: (p >> 8) & 0xff };
  if (k === 'Wait') return { kind: k, wait: (['SaveGame', 'ExitMatch', 'Fade'] as const)[(p >> 8) & 0xff] };
  return { kind: k };
}

/** The land bitmaps: 4096 rows of 1024 bytes, row j = world y j - 2048, bit b of byte k = x 8k + b - 4096. */
export const LAND_W = 8192, LAND_H = 4096, LAND_X0 = -4096, LAND_Y0 = -2048;
